import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FolderOpen } from "lucide-react";
import type { Project } from "../../api/projects";

export interface ProjectContextStripProps {
  project: Pick<Project, "id" | "name" | "instructions"> | null;
}

/** Slim strip beneath the chat header shown only when the session belongs
 *  to a project. Surfaces the project name and a one-line peek at its
 *  custom instructions so it's clear the assistant is scoped to a project. */
export function ProjectContextStrip({ project }: ProjectContextStripProps) {
  if (!project) return null;
  const peek = (project.instructions ?? "").trim();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.06 : 0.08),
        color: "text.secondary",
        minWidth: 0,
        backgroundImage: (theme) =>
          theme.palette.mode === "light"
            ? "linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0.12))"
            : "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0))",
      }}
    >
      <Box sx={{ color: "primary.main", display: "flex", flexShrink: 0 }}><FolderOpen size={14} aria-hidden="true" /></Box>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.primary", flexShrink: 0, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        Project scope
      </Typography>
      <Typography variant="caption" sx={{ color: "text.primary", fontWeight: 600, flexShrink: 0 }}>
        {project.name}
      </Typography>
      {peek && (
        <Typography variant="caption" noWrap sx={{ color: "text.secondary", minWidth: 0 }}>
          · {peek}
        </Typography>
      )}
    </Box>
  );
}
